/**
 * useDialogoAcessivel — o contrato de teclado e foco de todo painel sobreposto.
 *
 * Copiado do SAC (`sac/apps/web/src/lib/dialogo-acessivel.ts`, 09/09/2026) em
 * 10/10/2026 para o Sentinela, que ainda não tinha equivalente (o `Drawer` tem a
 * própria lógica interna; este hook serve ao que vive FORA da árvore dele —
 * hoje, `confirmar()`, `perguntar()` e `avisar()` em `./confirmar.tsx`).
 *
 * ⚠️ POR QUE EXISTE (auditoria do SAC com a skill web-accessibility): dos 19
 * painéis sobrepostos da área do cliente, **só 2 levavam o foco para dentro** ao
 * abrir. Os outros abriam e o foco continuava na página de trás, atrás do fundo
 * escurecido: quem navega por teclado seguia tabulando o conteúdo anterior, sem
 * nenhum sinal de que algo tinha aberto. E ao fechar, o foco reiniciava no topo —
 * a pessoa perdia o lugar na lista. O padrão era repetido à mão em cada
 * componente, que é exatamente como o próximo painel nasce errado de novo.
 *
 * O que este hook garante, e por quê:
 *
 *  1. **Foco entra no PRÓPRIO painel** (não no botão Fechar): o leitor de tela
 *     anuncia o nome do diálogo antes do conteúdo, e a primeira tabulação já
 *     cai no primeiro controle de dentro. Focar o Fechar faria a primeira coisa
 *     ouvida ser "Fechar" — a saída antes da entrada.
 *  2. **Foco VOLTA de onde saiu** ao fechar, se aquele elemento ainda existir.
 *  3. **Esc fecha**, salvo quando o chamador bloqueia (ex.: formulário
 *     preenchido — fechar sem querer perderia o que a pessoa digitou).
 *  4. **Fundo não rola** enquanto o painel está aberto.
 *  5. **Foco contido (opcional, `conterFoco`)**: Tab e Shift+Tab dão a volta dentro do painel em vez de
 *     sair para a página de trás. Portado do SAC (12/10/2026) para a gaveta do menu, que é MODAL de
 *     verdade (`aria-modal` sozinho não prende o foco do teclado: medido no Sentinela em 11/10/2026,
 *     a gaveta fechada deixava 13 itens na tabulação e a aberta deixava Tab chegar ao fundo). Fica
 *     opt-in: `confirmar()`/`perguntar()` têm o próprio tratamento e não foram mexidos.
 *  6. **Foco inicial escolhido (opcional, `focoInicial`)**: seletor CSS dentro do painel que recebe o
 *     foco ao abrir (o título da gaveta; o campo da busca "Ir para…"). Sem ele, o painel.
 *
 * Uso:
 *   const ref = useDialogoAcessivel<HTMLDivElement>({ aberto, aoFechar });
 *   <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true"
 *        aria-labelledby="titulo-x" className="… outline-none">
 *
 * ⚠️ O hook NÃO põe os atributos por você: `role`, `aria-modal` e o NOME
 * (`aria-label` ou `aria-labelledby`) ficam no JSX, onde se lê o que o painel é.
 *
 * ⛔ `aberto: true` FIXO só em componente que SÓ EXISTE quando está aberto
 * (`{x && <Painel/>}`). Componente montado o tempo todo deve passar o ESTADO
 * real: com `true` o hook põe `body.overflow = hidden` para sempre e a página
 * congela. E `aoFechar` precisa ser estável: função inline nova a cada render
 * reexecuta o efeito e tira o foco do painel.
 *
 * ⚠️ `useRef<T>(null)` (e não `<T | null>`): com os tipos do React 18 o atributo
 * `ref` do elemento só aceita `RefObject<T>`, e o erro aparece no ponto de USO.
 */
import { useEffect, useRef, type RefObject } from "react";

export interface OpcoesDialogo {
  /** O painel está aberto? Quando falso, o hook não faz nada. */
  aberto: boolean;
  /** Chamado no Esc. Omitir desliga o Esc. */
  aoFechar?: () => void;
  /**
   * Impede o Esc de fechar — use quando fechar sem querer causa perda de
   * trabalho (formulário preenchido, operação em voo).
   */
  travarEsc?: boolean;
  /** Deixe falso para não bloquear a rolagem do fundo (painel não modal). */
  travarRolagem?: boolean;
  /** Prende Tab/Shift+Tab dentro do painel (painel modal de verdade). */
  conterFoco?: boolean;
  /** Seletor CSS (dentro do painel) do elemento que recebe o foco ao abrir. Padrão: o próprio painel. */
  focoInicial?: string;
}

const FOCAVEIS =
  'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useDialogoAcessivel<T extends HTMLElement>({
  aberto,
  aoFechar,
  travarEsc = false,
  travarRolagem = true,
  conterFoco = false,
  focoInicial,
}: OpcoesDialogo): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!aberto) return;

    const anterior = document.activeElement as HTMLElement | null;
    // rAF: o painel pode ainda não estar pintado no mesmo tick em que abre.
    const id = requestAnimationFrame(() => {
      const alvo = focoInicial ? ref.current?.querySelector<HTMLElement>(focoInicial) : null;
      (alvo ?? ref.current)?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !travarEsc && aoFechar) aoFechar();
      if (e.key === "Tab" && conterFoco && ref.current) {
        const painel = ref.current;
        const itens = [...painel.querySelectorAll<HTMLElement>(FOCAVEIS)].filter((el) => el.getClientRects().length > 0);
        if (itens.length === 0) { e.preventDefault(); painel.focus(); return; }
        const primeiro = itens[0]!;
        const ultimo = itens[itens.length - 1]!;
        const atual = document.activeElement;
        // O foco inicial pode ser um elemento `tabindex=-1` (o título) que NÃO está em `itens`: de lá,
        // Shift+Tab sairia do painel, então ele conta como "antes do primeiro", igual ao próprio painel.
        const noInicio = atual === painel || (!!focoInicial && atual === painel.querySelector(focoInicial));
        if (e.shiftKey && (atual === primeiro || noInicio)) { e.preventDefault(); ultimo.focus(); }
        else if (!e.shiftKey && atual === ultimo) { e.preventDefault(); primeiro.focus(); }
        else if (!painel.contains(atual)) { e.preventDefault(); primeiro.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);

    const rolagemAntes = travarRolagem ? document.body.style.overflow : null;
    if (travarRolagem) document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("keydown", onKey);
      if (rolagemAntes !== null) document.body.style.overflow = rolagemAntes;
      // Só devolve o foco se o elemento ainda existe: em navegação por rota o
      // gatilho pode ter saído do DOM, e focar um órfão joga o foco no <body>.
      if (anterior && document.contains(anterior)) anterior.focus();
    };
  }, [aberto, aoFechar, travarEsc, travarRolagem, conterFoco, focoInicial]);

  return ref;
}
